import _ from 'underscore';

import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { Constants, TaxUtils, AccountUtils, AccountIdentityVerificationUtils } from '@bingads-webui-clientcenter/entity-utils';
import entityUtilLegacyString from '@bingads-webui-clientcenter/entity-utils-legacy-strings';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';

import { currentCid, currentUid, isAccountItemRow, isAdAccount, isManagerAccount } from '../common-utils';
import { getSharedColumnConfiguration } from './shared-column-configuration';
import { EmptyColumnValue } from '../constants';

import actionsColumnTemplate from '../template/vnext/actions-column.pug';
import businessLocationColumnTemplate from '../template/vnext/business-location-column.pug';
import taxIdColumnTemplate from '../template/vnext/tax-id-column.pug';
import textOverflowEllipsisTemplate from '../template/vnext/text-overflow-ellipsis.pug';
import accountVerificationStatusColumnTemplate from '../template/vnext/account-verification-status-column.pug';

const i18nGlobal = globalContext.require('$/i18n!legacy/global');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/accounts-summary');
const { getCCUrl: ccUrl } = globalContext.require('component/bingads-grid/shared/clientcenter-url-helper');
const globalConfig = globalContext.require('component/config/index');
const { columnNames } = getSharedColumnConfiguration(globalConfig);

const { isDeleted: isAccountDeleted } = AccountUtils;
const {
  IsEditAccountEnabled,
  dynamic,
} = globalConfig.get('Permissions');

const adAccountsSettingsUrl = 'settings/account';
const billingUrl = 'billing';
const managerAccountsSettingsUrl = 'settings/manageraccount';
const accountsUpdateSegments = 'accounts/update';
const IsChinaMarket = dynamic && dynamic.CNMarketExpansion;
const { getAccountAIVStatusForUI } = AccountIdentityVerificationUtils;

const formatAddressBusinessLocation = (accountItem) => {
  const emptyTextValue = { text: EmptyColumnValue };

  if (!isAdAccount(accountItem)) {
    return emptyTextValue;
  }

  const { Id, Country, PaymentInstrumentType } = accountItem;
  const localizedCountry = i18nGlobal.getString(`UserCountry_${Country}`);

  if (!_.isEmpty(localizedCountry)) {
    return {
      text: localizedCountry,
    };
  }

  if (PaymentInstrumentType !== Constants.PaymentInstrumentType.Invoice &&
    !isAccountDeleted(accountItem) &&
    IsEditAccountEnabled && _.isNumber(Id)) {
    return {
      text: i18n.getString('Account_BusinessLocation_EditLink'),
      url: ccUrl(accountsUpdateSegments, { accountIds: [Id], cid: currentCid(), uid: currentUid() }),
    };
  }
  return emptyTextValue;
};

// TODO: move to entity-utils constant
const VatStatusEnum = {
  Valid: 174,
  Invalid: 175,
  Pending: 176,
  PastDue: 177,
};

// TODO: move to entity-utils TaxUtils and give a doc of the background
const getTaxInfoStatusLocalized = (taxInfoArray, businessLocationCountry, vatStatus) => {
  const statusCss = vatStatus === VatStatusEnum.Invalid || vatStatus === VatStatusEnum.PastDue ? 'status-inactive' : '';
  // If we have tax information, then we can trust the country is texable country because we have this validation when update tax information. So we can show status depending on vatStatus when country and tax info are not empty.
  const statusText = !_.isEmpty(businessLocationCountry) && !_.isEmpty(taxInfoArray) && _.isNumber(vatStatus) ? i18n.getString(`AccountGrid_TaxInformation_VatStatus_${vatStatus}`) : '';

  return {
    statusCss,
    statusText,
  };
};

const formatTaxIdColumn = (accountItem, newI18n) => {
  const emptyTaxInfo = { taxInfoArray: null };

  if (!isAdAccount(accountItem)) {
    return emptyTaxInfo;
  }

  const { Country, TaxInformation, VatStatus } = accountItem;
  const entityUtilLegacyI18n = buildLegacyI18n(newI18n, [{ ...entityUtilLegacyString, Account_RFCLabel: _TL_('RFC') }]);

  if (!_.isEmpty(Country) && !_.isEmpty(TaxInformation)) {
    const taxInfoArray = TaxUtils.mapToLocalizedTaxInfos(TaxInformation, entityUtilLegacyI18n, { countryCode: Country });
    // TODO: use TaxUtils.getTaxInfoStatusLocalized
    const { statusCss, statusText } = getTaxInfoStatusLocalized(taxInfoArray, Country, VatStatus);

    return {
      taxInfoArray,
      statusCss,
      statusText,
    };
  }
  return emptyTaxInfo;
};

export const managementTabColumnsConfig = ({
  column, isForVNext, i18n: newI18n,
}) => _.compact([
  column({
    name: 'AccountVerificationStatus',
    title: 'ColumnName_AccountVerificationStatus',
    sortable: 'AccountVerificationStatus',
    type: 'string',
    property: {
      get({ AccountVerificationStatus }) {
        // when the AIV status is invalid, MT gives a null value; when in the first/last row, the AIV status is undefined
        return AccountVerificationStatus !== undefined ?
          getAccountAIVStatusForUI({ i18n: newI18n, accountVerificationStatus: AccountVerificationStatus })
          : EmptyColumnValue;
      },
      key: ['AccountVerificationStatus'],
    },
    template: accountVerificationStatusColumnTemplate,
    helpId: IsChinaMarket ? 'pop_BA_Accounts_AIVChina_AIVStatus' : 'pop_BA_Accounts_AIV_Status',
  }),
  column({
    name: columnNames.Balance,
    title: 'ColumnName_Balance',
    sortable: 'Balance',
    type: 'string',
    property: {
      key: ['Currency', 'Balance'],
      get(accountItem) {
        return isAdAccount(accountItem) ? i18n.formatCurrency(parseFloat(accountItem.Balance) || 0, accountItem.Currency) : EmptyColumnValue;
      },
    },
  }),
  column({
    name: columnNames.TaxId,
    title: 'ColumnName_TaxId',
    sortable: 'TaxInformationSort', // This value is used for sorting TaxInformation. No need in response.
    type: 'string',
    property: {
      key: ['TaxInformation', 'VatStatus', 'Country'],
      get(accountItem) {
        return formatTaxIdColumn(accountItem, newI18n);
      },
    },
    template: taxIdColumnTemplate,
    // Remove this since we don't have it in old accounts list and the infos are not match.
    // helpId: 'pop_BA_Accounts_TaxInfo',
  }),
  column({
    name: columnNames.PaymentSetting,
    title: 'ColumnName_PaymentSetting',
    sortable: 'PaymentType',
    type: 'string',
    property: {
      key: ['PaymentType'],
      get(accountItem) {
        return isAdAccount(accountItem) ? i18nGlobal.getString(`PaymentType_${accountItem.PaymentType}`) || EmptyColumnValue : EmptyColumnValue;
      },
    },
    helpId: 'Page_Accounts_Index_PaymentSetting',
  }),
  column({
    name: columnNames.BillingLanguage,
    title: 'ColumnName_BillingLanguage',
    sortable: false, // No need to sort since we don't have it in old accounts list
    type: 'string',
    property: {
      get(accountItem) {
        return isAdAccount(accountItem) ? i18nGlobal.getString(`Dimension_Lang_${accountItem.LanguageType}`) || EmptyColumnValue : EmptyColumnValue;
      },
      key: ['LanguageType'],
    },
  }),
  column({
    name: 'BusinessLegalName',
    title: 'ColumnName_BusinessLegalName',
    sortable: 'BusinessLegalName',
    type: 'string',
    property: 'BusinessLegalName',
    bodyClasses: ['business-legal-name-cell'],
    template: textOverflowEllipsisTemplate,
  }),
  column({
    name: columnNames.BusinessLocation,
    title: 'ColumnName_BusinessLocation',
    sortable: 'Country',
    type: 'string',
    property: {
      get(accountItem) {
        return formatAddressBusinessLocation(accountItem);
      },
      key: ['Country', 'PaymentInstrumentType'],
    },
    helpId: 'pop_BA_Accounts_BusinessLocation',
    template: businessLocationColumnTemplate,
  }),
  column({
    name: columnNames.AccountTimeZone,
    title: 'ColumnName_AccountTimeZone',
    sortable: 'TimeZone',
    type: 'string',
    property: {
      get({ TimeZone }) {
        return i18nGlobal.getString(`TimeZoneId_${TimeZone}`) || EmptyColumnValue;
      },
      key: ['TimeZone'],
    },
    bodyClasses: ['time-zone-cell', 'nowrap'],
  }),
  column({
    name: columnNames.PaymentMethod,
    title: 'ColumnName_PaymentMethod',
    sortable: 'PaymentInstrumentType',
    type: 'string',
    property: {
      get(accountItem) {
        return isAdAccount(accountItem) ? i18nGlobal.getString(`PaymentInstrTypeId_${accountItem.PaymentInstrumentType || 0}`) : EmptyColumnValue;
      },
      key: ['PaymentInstrumentType'],
    },
    bodyClasses: ['payment-method-cell', 'nowrap'],
  }),
  column({
    name: columnNames.AutoTagging,
    title: 'ColumnName_AutoTagging',
    sortable: 'AutoTagType',
    type: 'string',
    property: {
      get(accountItem) {
        const autoTaggingTypeMapping = {
          0: 'OFF',
          1: 'CustomerOverride',
          2: 'BingAdsOverride',
        };

        return isAdAccount(accountItem) ? i18n.getString(`Account_AutoTagging_${autoTaggingTypeMapping[accountItem.AutoTagType]}`) || EmptyColumnValue : EmptyColumnValue;
      },
      key: ['AutoTagType'],
    },
  }),
  column({
    name: columnNames.PrimaryContact,
    title: 'ColumnName_PrimaryContact',
    sortable: true,
    type: 'string',
    property: 'PreferredUserName',
    bodyClasses: ['primary-contact-cell', 'nowrap'],
    template: isForVNext ? textOverflowEllipsisTemplate : undefined,
  }),
  !isForVNext && column({
    name: columnNames.Actions,
    title: 'ColumnName_Actions',
    sortable: false,
    type: 'string',
    template: actionsColumnTemplate,
    property: {
      get(account) {
        if (!isAccountItemRow(account)) {
          return { emptyValue: EmptyColumnValue };
        }

        const common = {
          billingTitle: i18n.getString('BillingAndPayment_Settings_Title'),
        };

        if (isManagerAccount(account)) {
          return {
            ...common,
            accountSettingsHref: ccUrl(managerAccountsSettingsUrl, { cid: account.Id }),
            accountSettingsTitle: i18n.getString('ManagerAccount_Settings_Title'),
            billingHref: ccUrl(billingUrl, { cid: account.Id }),
          };
        }

        // account row
        const cid = account.IsParentAuthorized ? account.OwnerId : currentCid();
        return {
          ...common,
          accountSettingsHref: ccUrl(adAccountsSettingsUrl, { cid, aid: account.Id }),
          accountSettingsTitle: i18n.getString('AdAccount_Settings_Title'),
          billingHref: ccUrl(billingUrl, { cid, aid: account.Id }),
        };
      },
    },
    bodyClasses: ['actions-cell', 'nowrap'],
  }),
]);
